import React, { Dispatch } from "react";
import { useTheme } from "cayo.ui";
import { Toggle } from "@fluentui/react/lib/Toggle";
import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import { messages } from "../../messages";
import { MessageBar, MessageBarType, Icon } from "@fluentui/react";
import { IUpdateWizardAction } from "../../reducer";
import { INewManagedTenantState } from "../../types/INewManagedTenantState";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

interface ISelectServices {
  dispatchWizardState: Dispatch<IUpdateWizardAction>;
  wizardState: INewManagedTenantState;
}

const SelectServices = ({ wizardState, dispatchWizardState }: ISelectServices) => {
  const intl = useIntl();
  const { createError, connectionIsRecommended } = wizardState;
  const { cayoTheme } = useTheme();

  return (
    <div>
      {createError && (
        <MessageBar messageBarType={MessageBarType.error} truncated={false} isMultiline>
          {createError}
        </MessageBar>
      )}
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            margin: "10px 0 20px 0",
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        <FormattedMessage
          id={
            connectionIsRecommended
              ? messages.selectService365Banner.id
              : messages.selectService365BannerOld.id
          }
          defaultMessage={
            connectionIsRecommended
              ? messages.selectService365Banner.defaultMessage
              : messages.selectService365BannerOld.defaultMessage
          }
          values={{
            link: (
              <a
                href={"https://support.cayosoft.com/hc/en-us/articles/37330345941901"}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage
                  id={messages.selectService365BannerLink.id}
                  defaultMessage={messages.selectService365BannerLink.defaultMessage}
                />
              </a>
            ),
          }}
        />
      </MessageBar>
      <Text>{intl.formatMessage(messages.selectServiceEnableCollection)}:</Text>
      <Box>
        <Record>
          <div className="control">
            <Toggle
              checked={wizardState.systemTypes?.entra}
              disabled
              label={
                <Tooltip>
                  {intl.formatMessage(messages.selectServiceEntraId)} &nbsp;
                  <IconWithTooltip tooltipText={intl.formatMessage(messages.entraTooltip)} />
                </Tooltip>
              }
              onChange={(_, checked) =>
                dispatchWizardState({
                  action: "setSystemType",
                  newState: {
                    systemTypes: {
                      entra: checked!,
                    },
                  },
                })
              }
            />
          </div>
        </Record>
        <Record>
          <div className="control">
            <Toggle
              checked={wizardState.systemTypes?.exchangeOnline}
              label={
                <Tooltip>
                  {intl.formatMessage(messages.selectServiceExchangeOnline)} &nbsp;
                  <IconWithTooltip
                    tooltipText={intl.formatMessage(messages.exchangeOnlineTooltip)}
                  />
                </Tooltip>
              }
              onChange={(_, checked) =>
                dispatchWizardState({
                  action: "setSystemType",
                  newState: {
                    systemTypes: {
                      exchangeOnline: checked!,
                    },
                  },
                })
              }
            />
          </div>
        </Record>
        <Record>
          <div className="control">
            <Toggle
              checked={wizardState.systemTypes?.teams}
              label={
                <Tooltip>
                  {intl.formatMessage(messages.selectServiceTeams)} &nbsp;
                  <IconWithTooltip tooltipText={intl.formatMessage(messages.teamsTooltip)} />
                </Tooltip>
              }
              onChange={(_, checked) =>
                dispatchWizardState({
                  action: "setSystemType",
                  newState: {
                    systemTypes: {
                      teams: checked!,
                    },
                  },
                })
              }
            />
          </div>
        </Record>
        <Record>
          <div className="control">
            <Toggle
              checked={wizardState.systemTypes?.intune}
              label={
                <Tooltip>
                  {intl.formatMessage(messages.selectServiceIntune)} &nbsp;
                  <IconWithTooltip tooltipText={intl.formatMessage(messages.intuneTooltip)} />
                </Tooltip>
              }
              onChange={(_, checked) =>
                dispatchWizardState({
                  action: "setSystemType",
                  newState: {
                    systemTypes: {
                      intune: checked!,
                    },
                  },
                })
              }
            />
          </div>
        </Record>
      </Box>

      <NoteWrap>
        <span>{intl.formatMessage(messages.addNote)}</span>&nbsp;
        <a
          href="https://support.cayosoft.com/hc/en-us/articles/36184015460749"
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.addNoteLink)}
        </a>
      </NoteWrap>
    </div>
  );
};
const IconWithTooltip = ({ tooltipText }: { tooltipText: string }) => (
  <>
    <TooltipHost content={`${tooltipText}`} id={tooltipText}>
      <Icon iconName={"Info"} aria-describedby={tooltipText} />
    </TooltipHost>
  </>
);

const NoteWrap = styled.div`
  font-weight: 600;
`;

const Tooltip = styled.div`
  display: flex;
  align-items: center;
`;

const Record = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 500;
  margin-bottom: 10px;

  .control {
    > div {
      display: flex;
      flex-direction: row-reverse;
      align-items: baseline;
      gap: 5px;
    }
  }
`;

const Box = styled.div`
  margin-top: 20px;
`;

const Text = styled.p`
  margin-top: 20px;
`;

export default SelectServices;
